import { format, isValid, parse } from 'date-fns';
import type {
  CustomRunSummary,
  RunReview,
  TriggerSchedule,
} from '@/types/custom-playbooks';
import type { AgentDto, TriggerDto, TriggerInput } from '@/types/gtm-agents';
import { browserTimezone } from '@/components/builder/timezone.util';

/**
 * Scheduled briefs (W2-24 to W2-26, W2-49). An agent with nothing
 * to process runs on a schedule: its Instructions say what goes into the
 * brief, and the agent shows a Trigger for when. The engine runs it on an
 * internal play (a schedule trigger, the agent's turn, a Notify), which the
 * Play tab never lists. Its runs open in the Runs tab, with the brief, the
 * review line and Open chat.
 */

export type BriefKind = 'schedule' | 'one_time';

/** A brief's trigger: kind schedule or one_time on its play. */
export function isBriefTrigger(
  trigger: Pick<TriggerDto, 'kind' | 'source'>
): boolean {
  return (
    trigger.source === 'play' &&
    (trigger.kind === 'schedule' || trigger.kind === 'one_time')
  );
}

/**
 * A PATCH entry the server reads as the agent's brief (`_wants_brief`): kind
 * schedule or one_time, with no config or a schedule source. Without a
 * `playbookId` it edits the agent's brief, or creates the brief's play.
 */
export function isBriefEntry(
  entry: Pick<TriggerInput, 'kind' | 'config'>
): boolean {
  const source = entry.config?.source;
  return (
    (entry.kind === 'schedule' || entry.kind === 'one_time') &&
    (!entry.config ||
      Object.keys(entry.config).length === 0 ||
      source === 'schedule')
  );
}

/** The agent's brief plays, which the Play tab doesn't list. */
export function briefPlayIds(
  agent: Pick<AgentDto, 'playIds' | 'triggers'>
): string[] {
  const briefs = new Set(
    agent.triggers.filter(isBriefTrigger).map((trigger) => trigger.id)
  );
  return agent.playIds.filter((id) => briefs.has(id));
}

/** The plays the Play tab lists: every play but the briefs. */
export function recordPlayIds(
  agent: Pick<AgentDto, 'playIds' | 'triggers'>
): string[] {
  const briefs = new Set(briefPlayIds(agent));
  return agent.playIds.filter((id) => !briefs.has(id));
}

/** A run of one of the agent's briefs. */
export function isBriefRun(
  agent: Pick<AgentDto, 'playIds' | 'triggers'>,
  run: Pick<CustomRunSummary, 'playbookId'>
): boolean {
  return briefPlayIds(agent).includes(run.playbookId);
}

/** A new brief's schedule: weekly on Monday at 09:00, in the member's zone. */
export function defaultBriefSchedule(kind: BriefKind): TriggerSchedule {
  if (kind === 'one_time')
    return {
      frequency: 'once',
      date: format(new Date(Date.now() + 86_400_000), 'yyyy-MM-dd'),
      time: '09:00',
      timezone: browserTimezone(),
    };
  return {
    frequency: 'weekly',
    weekday: 0,
    time: '09:00',
    timezone: browserTimezone(),
  };
}

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Now on `timezone`'s wall clock as 'yyyy-MM-ddTHH:mm'; null for a bad zone. */
function wallClock(timezone: string): string | null {
  try {
    const part = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      })
        .formatToParts(new Date())
        .map((item) => [item.type, item.value])
    );
    return `${part.year}-${part.month}-${part.day}T${part.hour}:${part.minute}`;
  } catch {
    return null;
  }
}

/** Whether a brief's schedule can be saved as it stands. */
export function briefScheduleReady(schedule: TriggerSchedule): boolean {
  if (!schedule.time || !TIME.test(schedule.time)) return false;
  switch (schedule.frequency) {
    case 'once': {
      // As the server checks it: a date and time still to come in the
      // schedule's own zone (UTC when it names none).
      const day = schedule.date
        ? parse(schedule.date, 'yyyy-MM-dd', new Date())
        : null;
      const now = wallClock(schedule.timezone || 'UTC');
      return Boolean(
        day && isValid(day) && now && `${schedule.date}T${schedule.time}` > now
      );
    }
    case 'weekly':
      return typeof schedule.weekday === 'number';
    case 'every_n_days':
      return (
        typeof schedule.everyNDays === 'number' &&
        schedule.everyNDays >= 2 &&
        schedule.everyNDays <= 30
      );
    case 'monthly':
      return (
        schedule.firstBusinessDay === true ||
        typeof schedule.dayOfMonth === 'number'
      );
    case 'daily':
    case 'weekdays':
    case 'quarterly':
      return true;
    default:
      return false;
  }
}

/**
 * The PATCH entry for a brief: its kind and schedule. The server keeps the
 * agent's Updates destination and creates the brief's play when the agent
 * has none yet; an existing brief's play is edited in place.
 */
export function briefEntry(
  schedule: TriggerSchedule,
  existing?: Pick<TriggerDto, 'id' | 'note' | 'enabled'>
): TriggerInput {
  const kind: BriefKind =
    schedule.frequency === 'once' ? 'one_time' : 'schedule';
  return {
    id: existing?.id ?? '',
    kind,
    config: { source: 'schedule', kind },
    schedule,
    note: existing?.note ?? null,
    enabled: existing?.enabled ?? false,
    source: 'play',
  };
}

/** A brief trigger's schedule as the form edits it. */
export function briefScheduleOf(
  trigger: Pick<TriggerDto, 'kind' | 'schedule' | 'config'>
): TriggerSchedule {
  if (trigger.schedule) return { ...trigger.schedule };
  const config = trigger.config as {
    date?: string;
    time?: string;
    timezone?: string;
  };
  if (trigger.kind === 'one_time')
    return {
      ...defaultBriefSchedule('one_time'),
      ...(config.date ? { date: config.date } : {}),
      ...(config.time ? { time: config.time } : {}),
      ...(config.timezone ? { timezone: config.timezone } : {}),
    };
  return defaultBriefSchedule('schedule');
}

/** The run line's last words: the review's line (a brief's first line). */
export function reviewLine(
  run: Pick<CustomRunSummary, 'review'>
): string | undefined {
  return run.review?.line || undefined;
}

/** "312 credits · Data 300 · Clu 12", only when Clu spent something. */
export function creditsSplit(
  review: Pick<RunReview, 'credits'> | null | undefined
): string | undefined {
  if (!review) return undefined;
  const { data, clu } = review.credits;
  if (!clu) return undefined;
  return 'Data ' + data.toLocaleString() + ' · Clu ' + clu.toLocaleString();
}
